import Modal from '@/components/_common/Modal/Modal';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppSelector } from '@/redux/hooks';
import { selectAllLanguagesMap } from '@/redux/selectors/constantsSelectors';
import { selectFocusGroupedRightsValidateByVersionId } from '@/redux/selectors/focusNewScheduleSelectors';
import styled from '@emotion/styled';
import { alpha, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';

type RightsRestrictionProps = {
  id?: number;
  launchId?: string;
  schedulingId?: number;
};

const RightsRestriction: FC<RightsRestrictionProps> = ({ id, launchId, schedulingId }) => {
  const allLanguagesMap = useAppSelector(selectAllLanguagesMap);
  const [open, setOpen] = useState(false);

  const versionId = id ?? schedulingId!;

  const displayValues = useAppSelector((state) => selectFocusGroupedRightsValidateByVersionId(state, versionId, launchId));
  const regionNamesInEnglish = useMemo(() => new Intl.DisplayNames(['en'], { type: 'region' }), []);

  const onClose = () => setOpen(false);

  const getTerritoryLabel = (ter: string) => {
    const upper = ter.toUpperCase();
    return `${regionNamesInEnglish.of(upper) ?? ter} [${upper}]`;
  };

  const displayLanguage = (code: string) => {
    const label = allLanguagesMap?.[code.replace('-', '_')]?.label;
    return `${label ?? code} [${code}]`;
  };

  if (!displayValues.length) {
    return null;
  }

  return (
    <>
      <Tooltip title="Validation Errors">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          aria-label="Validation Errors"
        >
          <PolarisIcon icon="error" color="orange" fontSize={25} />
        </IconButton>
      </Tooltip>
      <Modal maxWidth={1440} noHeader open={open} onClose={onClose} scrollDisabled title="Rights Restrictions">
        <StyledHeader>
          <div>
            <StyledTitle variant="h2">
              <PolarisIcon icon="error" color="orange" /> Rights Restrictions
            </StyledTitle>
            <Typography>The following selections have rights restrictions, review before proceeding</Typography>
          </div>
          <StyledCloseButton onClick={onClose}>
            <StyledCloseIcon icon="close" />
          </StyledCloseButton>
        </StyledHeader>
        <StyledBody>
          <ScrollContainer>
            {displayValues.map((error, index) => (
              <ErrorContainer key={index}>
                <Typography variant="h6">Restricted Combinations</Typography>
                <TerritoryContainer>
                  {error.territories.map((ter) => (
                    <Territory key={ter}>{getTerritoryLabel(ter)}</Territory>
                  ))}
                </TerritoryContainer>
                <Typography variant="subtitle2" sx={{ m: '16px 0 12px 0' }}>
                  Languages not permitted:
                </Typography>
                <LanguagesContaner>
                  <div>
                    <Typography sx={{ mb: '8px' }}>Audio</Typography>
                    <LanguagesContainer>
                      {error.rights
                        .filter((r) => r.element_type === 'audio')
                        .map((right, idx) => (
                          <RightItem key={idx}>
                            <div>{displayLanguage(right.language)}</div>
                            <div>{right.reason.replace('_', ' ')}</div>
                          </RightItem>
                        ))}
                    </LanguagesContainer>
                  </div>
                  <div>
                    <Typography sx={{ mb: '8px' }}>Timed Text</Typography>
                    <LanguagesContainer>
                      {error.rights
                        .filter((r) => r.element_type === 'timedtext')
                        .map((right, idx) => (
                          <RightItem key={idx}>
                            <div>{displayLanguage(right.language)}</div>
                            <div>{right.reason.replace('_', ' ')}</div>
                          </RightItem>
                        ))}
                    </LanguagesContainer>
                  </div>
                </LanguagesContaner>
              </ErrorContainer>
            ))}
          </ScrollContainer>
          <StyledFooter>
            <Button color="secondary" onClick={onClose} variant="contained">
              Close
            </Button>
          </StyledFooter>
        </StyledBody>
      </Modal>
    </>
  );
};

export default RightsRestriction;

const StyledBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ScrollContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing(2)};
`;

const StyledCloseButton = styled(IconButton)`
  padding: 0;
`;

const StyledCloseIcon = styled(PolarisIcon)`
  font-size: 22px; // need to redefine in theme
  opacity: 0.6;
  color: ${({ theme }) => theme.palette.neutral[0]};

  &:hover {
    opacity: 1;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const StyledTitle = styled(Typography)`
  font-size: 28px; // need to redefine in theme
  font-weight: 700;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.neutral[0]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const ErrorContainer = styled.div`
  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const TerritoryContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1)} 0;
`;

const Territory = styled.div`
  background-color: ${({ theme }) => alpha(theme.palette.tertiary.main, 0.15)};
  padding: ${({ theme }) => theme.spacing(1)};
  border: 1px solid ${({ theme }) => theme.palette.tertiary.main};
  border-radius: 10px;
`;

const LanguagesContaner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const LanguagesContainer = styled.div`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const RightItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(1)};
`;
